/**
 * Builds human-readable calculation sources for weekly report figures.
 *
 * Every formula mirrors the backend accessors in
 * cafeteriat-api/app/Models/WeeklyReport.php (totalSales, totalPurchases,
 * totalCosts, netProfit, *Ratio). Keep both in sync when a rule changes.
 */
export default {
  methods: {
    wrNum(value) {
      return parseFloat(value || 0);
    },

    wrCurrency(value) {
      return this.$formatCurrency(this.wrNum(value)).trim();
    },

    wrFormulaTotalSales(report, withTaxes = false) {
      const sfx = withTaxes ? "_with_taxes" : "";
      const invoicing =
        this.wrNum(report[`invoicing_sales_total${sfx}`]) +
        this.wrNum(report[`extra_sales_total${sfx}`]);
      return this.$t("WEEKLY_REPORTS.FORMULA_TOTAL_SALES", {
        daily: this.wrCurrency(report[`daily_sales_total${sfx}`]),
        invoicing: this.wrCurrency(invoicing),
        internal: this.wrCurrency(
          report[withTaxes ? "internal_invoice_out_with_taxes" : "internal_invoice_out_total"]
        ),
        vending: this.wrCurrency(report[`sales_vending_machines${sfx}`]),
        total: this.wrCurrency(report[`total_sales${sfx}`]),
      });
    },

    wrFormulaTotalPurchases(report, withTaxes = false) {
      const sfx = withTaxes ? "_with_taxes" : "";
      const regular =
        this.wrNum(report[`total_purchases${sfx}`]) +
        this.wrNum(report[withTaxes ? "transfer_out_with_taxes" : "transfer_out_total"]) -
        this.wrNum(report[withTaxes ? "transfer_in_with_taxes" : "transfer_in_total"]) -
        this.wrNum(
          report[withTaxes ? "internal_invoice_in_with_taxes" : "internal_invoice_in_total"]
        );
      return this.$t("WEEKLY_REPORTS.FORMULA_TOTAL_PURCHASES", {
        regular: this.wrCurrency(regular),
        transferIn: this.wrCurrency(
          report[withTaxes ? "transfer_in_with_taxes" : "transfer_in_total"]
        ),
        transferOut: this.wrCurrency(
          report[withTaxes ? "transfer_out_with_taxes" : "transfer_out_total"]
        ),
        internalIn: this.wrCurrency(
          report[withTaxes ? "internal_invoice_in_with_taxes" : "internal_invoice_in_total"]
        ),
        total: this.wrCurrency(report[`total_purchases${sfx}`]),
      });
    },

    wrFormulaTotalCosts(report) {
      return this.$t("WEEKLY_REPORTS.FORMULA_TOTAL_COSTS", {
        wages: this.wrCurrency(report.wages_with_taxes),
        rent: this.wrCurrency(report.rent_with_taxes),
        total: this.wrCurrency(report.total_costs),
      });
    },

    wrFormulaNetProfit(report) {
      return this.$t("WEEKLY_REPORTS.FORMULA_NET_PROFIT", {
        sales: this.wrCurrency(report.total_sales),
        purchases: this.wrCurrency(report.total_purchases),
        costs: this.wrCurrency(report.total_costs),
        total: this.wrCurrency(report.net_profit),
      });
    },

    /**
     * Ratio = numerator / total_sales × 100 (backend divides by untaxed total_sales).
     */
    wrFormulaRatio(report, label, numerator, ratio) {
      return this.$t("WEEKLY_REPORTS.FORMULA_RATIO", {
        label,
        numerator: this.wrCurrency(numerator),
        sales: this.wrCurrency(report.total_sales),
        ratio: this.$formatPercentage(ratio),
      });
    },

    wrFormulaWagesRatio(report) {
      return this.wrFormulaRatio(
        report,
        this.$t("WEEKLY_REPORTS.WAGES"),
        report.wages_with_taxes,
        report.wages_ratio
      );
    },

    wrFormulaPurchasesRatio(report) {
      return this.wrFormulaRatio(
        report,
        this.$t("WEEKLY_REPORTS.TOTAL_PURCHASES"),
        report.total_purchases,
        report.purchases_ratio
      );
    },

    wrFormulaConsumablesRatio(report) {
      return this.wrFormulaRatio(
        report,
        this.$t("WEEKLY_REPORTS.PURCHASES_CONSUMABLES"),
        report.consumables_purchases_total,
        report.consumables_ratio
      );
    },
  },
};
